'use client';

import { Button } from '@/components/ui/button';
import { useCart } from '@/lib/cart/store';

interface AddToCartButtonProps {
  linkSku: string;
  disabled?: boolean;
  disabledLabel?: string;
  /** Idle label; defaults to "Add to cart" (e.g. "Add as backorder"). */
  label?: string;
  className?: string;
  size?: 'default' | 'sm' | 'lg';
  variant?: 'default' | 'outline';
  fullWidth?: boolean;
}

export function AddToCartButton({
  linkSku,
  disabled,
  disabledLabel,
  label = 'Add to cart',
  className,
  size = 'sm',
  variant = 'outline',
  fullWidth,
}: AddToCartButtonProps) {
  const { items, add, setQty } = useCart();
  const inCart = items[linkSku]?.qty ?? 0;

  const widthClass = fullWidth ? 'w-full' : '';

  if (disabled) {
    return (
      <Button
        type="button"
        variant={variant}
        size={size}
        className={`${widthClass} ${className ?? ''}`}
        disabled
      >
        {disabledLabel ?? 'Unavailable'}
      </Button>
    );
  }

  if (inCart > 0) {
    return (
      <div className={`flex items-center gap-2 ${widthClass} ${className ?? ''}`}>
        <Button
          type="button"
          variant="outline"
          size={size}
          onClick={() => setQty(linkSku, inCart - 1)}
          aria-label="Decrease quantity"
        >
          −
        </Button>
        <span className="min-w-8 text-center text-sm tabular-nums">{inCart}</span>
        <Button
          type="button"
          variant="outline"
          size={size}
          onClick={() => add(linkSku, 1)}
          aria-label="Increase quantity"
        >
          +
        </Button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={`${widthClass} ${className ?? ''}`}
      onClick={() => add(linkSku, 1)}
    >
      {label}
    </Button>
  );
}
